import { useEffect, useRef, useState } from 'react';
import { buildIgPayment, calcFeeCoverAmount, newPaymentToken } from '../igIframe';
import { logDonationEvent } from '../api';
import { useIgFrameHeight } from '../hooks/useIgFrameHeight';
import { useBitRedirect } from '../hooks/useBitRedirect';
import { readUtmParams } from '../utmParams';
import { resolvePurchaseValue, trackPurchase } from '../analytics';
import { donatePage } from '../data';
import { useEnglishTermsDialog, TermsLink } from './ConsentTerms';
import {
  currencyMeta, formatAmount, presetAmounts, COUNTRIES, countryName, CURRENCY_ORDER,
} from '../currencies';

// The English page's card donation for every currency except USD: choose an
// amount, leave donor details, then pay in the IsraelGives hosted frame — the
// same POST-into-iframe mechanism as DonatePage (igIframe.js), in English.
//
// The one deliberate difference: nothing reaches Gramsci. The donation is still
// logged on our server ('initiated' / 'completed', page 'en'), but the server's
// NO_CRM_PAGES skips the CRM write — the client has IsraelGives report English
// donations to them directly.

const PAGE = 'en';
const FRAME_NAME = 'ig-frame-en';
// "Friends of Standing Together" in the amuta's IsraelGives projects list
// (backoffice → פרויקטים וייעודים). Every English donation is tagged with it.
const IG_PROJECT_ID = '28714';
// The in-form switcher: the chooser's six tiles first, then — under a divider,
// per the client — every other currency the account can charge
// (CURRENCY_ORDER), alphabetically by name.
const MAIN_CURRENCIES = [
  { code: 'USD', name: 'US Dollars' },
  { code: 'GBP', name: 'Pound Sterling' },
  { code: 'CAD', name: 'Canadian Dollars' },
  { code: 'EUR', name: 'Euro' },
  { code: 'ILS', name: 'Israeli Shekels' },
  { code: 'AUD', name: 'Australian dollar' },
];
const OTHER_NAMES = {
  CHF: 'Swiss Franc', SEK: 'Swedish Krona', NOK: 'Norwegian Krone', DKK: 'Danish Krone',
  NZD: 'New Zealand Dollar', ZAR: 'South African Rand', PLN: 'Polish Zloty', CZK: 'Czech Koruna',
  HUF: 'Hungarian Forint', JPY: 'Japanese Yen', SGD: 'Singapore Dollar', HKD: 'Hong Kong Dollar',
  KRW: 'South Korean Won', BRL: 'Brazilian Real', MXN: 'Mexican Peso',
};
const OTHER_CURRENCIES = CURRENCY_ORDER
  .filter((code) => !MAIN_CURRENCIES.some((c) => c.code === code))
  .map((code) => ({ code, name: OTHER_NAMES[code] || code }))
  .sort((a, b) => a.name.localeCompare(b.name, 'en'));

const EMPTY = {
  firstName: '', lastName: '', email: '', phone: '', city: '',
  // Asked in every currency; street is required only for a monthly donation
  // (see validate), postcode and country never. IsraelGives' POST-fields doc
  // marks only fname, lname and email as required, and in the POSTed iframe
  // their CAD/GBP/EUR address rule (`AdressIsMandatory()`) only checks fields
  // that are visible — every address input is hidden there, so it never fires.
  street: '', zipCode: '', countryId: '',
};

// Monthly donors are asked for more than one-time ones: a phone number, city and
// street address are required, where a one-time donation needs only name + email.
function validate(form, recurring) {
  const e = {};
  if (!form.firstName.trim()) e.firstName = 'Please enter your first name';
  if (!form.lastName.trim()) e.lastName = 'Please enter your last name';
  if (!form.email.trim()) e.email = 'Please enter your email';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Please enter a valid email';
  // Phone is optional for a one-time gift, but a number that's there has to look like one.
  if (form.phone.trim()) {
    if (String(form.phone).replace(/\D/g, '').length < 7) e.phone = 'Please enter a valid phone number';
  } else if (recurring) {
    e.phone = 'Please enter your phone number';
  }
  if (recurring) {
    if (!form.city.trim()) e.city = 'Please enter your city';
    if (!form.street.trim()) e.street = 'Please enter your street address';
  }
  return e;
}

// `currency` is null when the donor came through the chooser's "Other" tile:
// only the currency dropdown shows until they pick one.
export default function IntlDonationForm({ currency, recurring, onChangeCurrency }) {
  const cur = currencyMeta(currency);
  const defaultCountry = currency ? cur.countryId : '';
  // Shekel presets come from the Hebrew donate page so ILS matches it; every
  // other currency has its own round numbers in currencies.js.
  const amounts = presetAmounts(currency, recurring,
    recurring ? donatePage.monthlyAmounts : donatePage.onceAmounts);

  const [amount, setAmount] = useState('');
  const [custom, setCustom] = useState('');
  const [form, setForm] = useState(() => ({ ...EMPTY, countryId: defaultCountry || '' }));
  const [errors, setErrors] = useState({});
  const [coverFee, setCoverFee] = useState(true);
  const [igSubmitted, setIgSubmitted] = useState(false);
  // Bumped on every submit, so a re-submit POSTs again — after the render
  // that put the new attempt's token into the hidden form.
  const [attempt, setAttempt] = useState(0);
  const [done, setDone] = useState(false);
  const [shownCurrency, setShownCurrency] = useState(currency);
  const [openTerms, termsDialog] = useEnglishTermsDialog();

  // The donor can switch currency without leaving the form (the dropdown
  // below, or the chooser's tiles). Their details stay; what was priced in the
  // old currency — the amount, the open payment frame, the default country —
  // does not. The country follows the currency (shekels: Israel).
  if (shownCurrency !== currency) {
    setShownCurrency(currency);
    setAmount('');
    setCustom('');
    setErrors({});
    setIgSubmitted(false);
    setForm((f) => ({ ...f, countryId: defaultCountry || f.countryId }));
  }

  const igFrameRef = useIgFrameHeight();
  useBitRedirect();
  const postFormRef = useRef(null);
  const paymentTokenRef = useRef('');
  const completedRef = useRef(false);
  const topRef = useRef(null);

  const customNum = Number(custom);
  const customInvalid = custom.trim() !== '' && (!Number.isFinite(customNum) || customNum < cur.min);
  const chosen = custom.trim() !== '' ? (customInvalid ? '' : custom.trim()) : amount;
  // As on the Hebrew pages, the donor details stay closed until there is an
  // amount. Once open they stay open — clearing the amount or switching
  // currency must not hide details already typed in.
  const [detailsOpen, setDetailsOpen] = useState(false);
  if (chosen && !detailsOpen) setDetailsOpen(true);
  const feeAmount = calcFeeCoverAmount(chosen, currency);
  const total = coverFee ? Math.round((Number(chosen) + feeAmount) * 100) / 100 : Number(chosen);
  // The message handler below is mounted once, so it reads the live currency
  // and total off a ref rather than the values from the first render.
  const latestDonor = useRef({});
  latestDonor.current = { currency, total };

  // The IsraelGives frame redirects to /ig-complete.html (same origin) after a
  // successful charge, and that page postMessages us. It also reports the
  // completion to our server itself, so this handler owns the UI and the
  // conversions.
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin || !e.data || e.data.type !== 'ig-donation-complete') return;
      // Acknowledge first: ig-complete.html sends the whole tab back to /en if
      // nobody answers, and a duplicate message still proves we're handling it.
      if (e.source) e.source.postMessage({ type: 'ig-donation-ack' }, window.location.origin);
      if (completedRef.current) return;
      completedRef.current = true;
      // The currency the donor chose, not a hardcoded one — reporting a $50
      // donation as ₪50 would misprice every conversion in Ads and GA4.
      const paidCurrency = latestDonor.current.currency;
      const paidValue = resolvePurchaseValue(latestDonor.current.total, e.data.sum);
      if (window.fbq) window.fbq('track', 'Purchase', { value: paidValue, currency: paidCurrency });
      if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: 'AW-718777808/kPpUCPHXhu0bENDb3tYC', transaction_id: e.data.donationId || '' });
      }
      // GA4 + the other GTM purchase tags — see analytics.js for why this isn't a gtag() call.
      trackPurchase({ transactionId: e.data.donationId, value: paidValue, currency: paidCurrency });
      setIgSubmitted(false);
      setDone(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Once the details are in, POST them into the frame so IsraelGives renders
  // its card fields there.
  useEffect(() => {
    if (igSubmitted && postFormRef.current) postFormRef.current.submit();
  }, [igSubmitted, attempt]);

  useEffect(() => {
    if (done && topRef.current && topRef.current.scrollIntoView) topRef.current.scrollIntoView({ block: 'nearest' });
  }, [done]);

  const setField = (key) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    // The open frame holds the details it was POSTed with; editing one means
    // re-submitting so the charge never differs from what's on screen.
    if (igSubmitted) setIgSubmitted(false);
  };

  const pickAmount = (value) => {
    setAmount(value);
    setCustom('');
    setErrors((prev) => ({ ...prev, amount: undefined }));
    if (igSubmitted) setIgSubmitted(false);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate(form, recurring);
    if (!chosen || Number(chosen) <= 0) {
      errs.amount = customInvalid
        ? `The minimum donation is ${formatAmount(cur.min, currency)}`
        : 'Please choose an amount';
    }
    setErrors(errs);
    if (Object.keys(errs).length) return;

    paymentTokenRef.current = newPaymentToken();
    // Our own record of the attempt. page 'en' keeps it out of Gramsci at
    // completion (server NO_CRM_PAGES).
    logDonationEvent({
      stage: 'initiated',
      token: paymentTokenRef.current,
      page: PAGE,
      firstName: form.firstName, lastName: form.lastName, email: form.email,
      phone: form.phone, city: form.city,
      amount: total, recurring, currency, coverFee,
      street: form.street, zipCode: form.zipCode, country: countryName(form.countryId),
      ...readUtmParams(),
    });
    setAttempt((n) => n + 1);
    setIgSubmitted(true);
  };

  if (done) {
    return (
      <div className="idf idf--done" ref={topRef} role="status">
        <h2 className="idf-title">Thank you!</h2>
        <p className="idf-sub">
          Your donation was received. A receipt is on its way to your email.
          Thank you for standing with us.
        </p>
      </div>
    );
  }

  const { action, fields } = buildIgPayment({
    profile: { ...form, address: form.street },
    amount: total,
    currency,
    frequency: recurring ? '2' : '1',
    projectId: IG_PROJECT_ID,
    token: paymentTokenRef.current,
    returnPath: window.location.pathname,
  });

  const field = (key, label, input, required = true) => (
    <label className={`odf-field${errors[key] ? ' has-error' : ''}`}>
      <span className="odf-label">{label}{required ? ' *' : ''}</span>
      {input}
      {errors[key] && <span className="odf-error">{errors[key]}</span>}
    </label>
  );

  return (
    <div className="idf" ref={topRef}>
      <form className="idf-details" onSubmit={onSubmit} noValidate>
        <div className="idf-head">
          <h2 className="idf-title">{recurring ? 'Become a Friend' : 'One-Time Donation'}</h2>
          <p className="idf-sub">
            {recurring ? 'Monthly donation' : 'One-time donation'} in{' '}
            <select
              className="idf-currency"
              aria-label="Currency"
              value={currency || ''}
              onChange={(e) => onChangeCurrency(e.target.value)}
            >
              {!currency && <option value="" disabled>Select a currency</option>}
              {MAIN_CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name} - {c.code}</option>
              ))}
              <option disabled>── Non-tax deductible currencies ──</option>
              {OTHER_CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name} - {c.code}</option>
              ))}
            </select>
          </p>
        </div>

        {!currency && <p className="idf-sub">Choose your currency above to continue.</p>}
        {currency && (
          <>
            <fieldset className="idf-amounts">
              <legend className="odf-label">Amount{recurring ? ' per month' : ''} *</legend>
              <div className="idf-amount-grid">
                {amounts.map((a) => (
                  <button
                    type="button"
                    key={a}
                    className={`idf-amount${!custom && amount === a ? ' is-active' : ''}`}
                    aria-pressed={!custom && amount === a}
                    onClick={() => pickAmount(a)}
                  >
                    {formatAmount(a, currency)}
                  </button>
                ))}
                <input
                  className={`idf-amount idf-other${custom.trim() ? ' is-active' : ''}${customInvalid ? ' is-invalid' : ''}`}
                  type="number"
                  inputMode="decimal"
                  min={cur.min}
                  placeholder={`Other (${cur.symbol})`}
                  aria-label="Other amount"
                  value={custom}
                  onChange={(e) => {
                    setCustom(e.target.value);
                    setAmount('');
                    setErrors((prev) => ({ ...prev, amount: undefined }));
                    if (igSubmitted) setIgSubmitted(false);
                  }}
                />
              </div>
              {/* An "Other" amount under the minimum is flagged as it's typed:
                  it keeps the details closed, so waiting for a submit would
                  leave the donor with nothing to press and no reason why. */}
              {customInvalid
                ? <span className="odf-error">The minimum donation is {formatAmount(cur.min, currency)}</span>
                : errors.amount && <span className="odf-error">{errors.amount}</span>}
            </fieldset>

            {detailsOpen && (
              <>
                <div className="odf-row">
                  {field('firstName', 'First Name',
                    <input autoComplete="given-name" value={form.firstName} onChange={setField('firstName')} />)}
                  {field('lastName', 'Last Name',
                    <input autoComplete="family-name" value={form.lastName} onChange={setField('lastName')} />)}
                </div>
                {field('email', 'Email',
                  <input type="email" autoComplete="email" value={form.email} onChange={setField('email')} />)}
                <div className="odf-row">
                  {field('phone', 'Phone No.',
                    <input type="tel" autoComplete="tel" value={form.phone} onChange={setField('phone')} />, recurring)}
                  {field('city', 'City',
                    <input autoComplete="address-level2" value={form.city} onChange={setField('city')} />, recurring)}
                </div>
                {field('street', 'Street Address',
                  <input autoComplete="street-address" value={form.street} onChange={setField('street')} />, recurring)}
                <div className="odf-row">
                  {field('zipCode', 'Postcode',
                    <input autoComplete="postal-code" maxLength={7} value={form.zipCode} onChange={setField('zipCode')} />,
                    false)}
                  {field('countryId', 'Country',
                    <select value={form.countryId} onChange={setField('countryId')} autoComplete="country-name">
                      <option value="">Select a Country</option>
                      {COUNTRIES.map((c) => <option key={c.id} value={c.id}>{c.en}</option>)}
                    </select>, false)}
                </div>

                <label className="idf-check">
                  <input type="checkbox" checked={coverFee} onChange={(e) => setCoverFee(e.target.checked)} />
                  <span>
                    I&apos;d like to cover the transaction fee
                    {chosen ? ` (${formatAmount(feeAmount, currency)})` : ''} so Standing Together receives my full donation
                  </span>
                </label>

                {/* Hidden while the frame is open: editing any detail closes the frame
                    (setField) and brings the button back. */}
                {!igSubmitted && (
                  <button type="submit" className="odf-submit">
                    {chosen ? `Continue to payment · ${formatAmount(total, currency)}${recurring ? ' / month' : ''}` : 'Continue to payment'}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </form>

      {/* Outside the details form — forms can't nest. The hidden form is always rendered so the effect above can POST it;
          the frame only appears once there is something to show. */}
      <div className="idf-hosted" hidden={!igSubmitted}>
        <form ref={postFormRef} method="POST" action={action} target={FRAME_NAME} className="mf-hosted-post">
          {Object.entries(fields).map(([name, value]) => (
            <input key={name} type="hidden" name={name} value={value} />
          ))}
        </form>
        <iframe ref={igFrameRef} className="mf-iframe" title="Secure payment" name={FRAME_NAME} />
      </div>

      <p className="odf-terms">Donations are tax-deductible in the US, UK, Canada and France</p>
      <p className="odf-terms">
        Payments are processed securely by IsraelGives. By donating I agree to the{' '}
        <TermsLink onOpen={openTerms}>terms and conditions</TermsLink>
      </p>
      {termsDialog}
    </div>
  );
}
